<script lang="tsx">
import { computed, defineComponent, ref, useId } from "vue";
  import { Button, FloatLabel, IconField, InputIcon, InputNumber, InputText, Message, MultiSelect, Select, Textarea, Tooltip } from "primevue";
  import { Form, FormField as PrimeFormField, type FormInstance, type FormResolverOptions, type FormSubmitEvent } from "@primevue/forms";
  import { useDebounceFn, useThrottleFn } from "@vueuse/core";

  import Editor from "./Editor.vue";
  import AssetsUpload from "./AssetsUpload.vue";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";

  import { useQuery } from "@urql/vue";
  import { graphql as gql } from "@/gql";
  import type { centreDirectoryEntry_Entry } from "@/gql/graphql";

  import { search, type ResultGeoProperties } from "@/lib/nominatim";
  import type { Feature, Geometry } from "geojson";
  import { useAbortFn } from "@/lib/util";

  import municipalRegions from "@/data/municipal_regions.json";
  import childcareAges from "@/data/childcare_ages.json";
  import type { RequestResult } from "@/lib/craft";
  import { formatPhoneNumber, formatPostalCode, isEmailValid, isPostalCodeValid, isUrlValid, resolvePhoneNumber } from "@/lib/forms";


  export default defineComponent({
    directives: {
      tooltip: Tooltip
    },
    components: {
      Form, PrimeFormField, FloatLabel, IconField, InputIcon, Button, Icon, Message,
      Editor, InputText, InputNumber, Textarea, Select, MultiSelect, AssetsUpload
    },
    props: {
      site: {
        type: String,
        required: true,
      },
      entryId: {
        type: Number,
      }
    },
    async setup(props) {
      const vid = useId();

      const { data } = await useQuery({
        query: gql(`
          query QueryCentreDirectoryEntryInitialInfo(
            $id: QueryArgument, $new: Boolean!
          ) {
            anyEntry: centreDirectoryEntries(limit: 1) @include(if: $new) {
              ... on EntryInterface {
                sectionId
              }
            }

            entry: centreDirectoryEntries(id: [$id]) @skip(if: $new) {
              ... on centreDirectoryEntry_Entry {
                sectionId

                title
                operator
                description {
                  html
                  rawHtml
                  markdown
                  plainText
                }
                address {
                  id
                  addressLines
                  country
                  state
                  province
                  region
                  city
                  postalCode
                  coordinates {
                    latitude
                    longitude
                  }
                }
                email
                phone {
                  number
                  international: format(format: "international")
                  tel: format(format: "rfc3966")
                }
                fax {
                  number
                  international: format(format: "international")
                  tel: format(format: "rfc3966")
                }
                website {
                  url
                }
                childcareAge
                logo {
                  id
                }
                images {
                  id
                }
              }
            }
          }
        `),
        variables: {
          id: props.entryId ?? null,
          new: props.entryId == null,
        },
      });
      await new Promise((acc) => {setTimeout(acc, 1500)});

      return {
        vid,
        entry: computed(() => data.value?.entry?.[0] ?? null),
        createNew: computed(() => props.entryId == null),
        sectionId: computed(
          () => data.value?.entry?.[0]?.sectionId ?? data.value?.anyEntry?.[0]?.sectionId ?? 14
        ),
      };
    },
    data() {
      // Vue simply cannot do type inference in data() for the public instance proxy
      // https://github.com/vuejs/vue/issues/12427
      // We need to stick on the extra attributes we query for phone numbers.
      const entry = this.entry as any as centreDirectoryEntry_Entry & {
        phone: { international?: string | null; tel?: string | null; }
        fax: { international?: string | null; tel?: string | null; }
      };

      return {
        initialValues: {
          title: entry?.title ?? null,
          operator: entry?.operator ?? null,
          addressLines: entry?.address?.addressLines ?? null,
          city: entry?.address?.city ?? null,
          postalCode: entry?.address?.postalCode ?? null,
          region: entry?.address?.region ?? null,
          longitude: entry?.address?.coordinates?.longitude ?? null,
          latitude: entry?.address?.coordinates?.latitude ?? null,
          email: entry?.email ?? null,
          website: entry?.website?.url ?? null,
          phone: entry?.phone?.international ?? null,
          fax: entry?.fax?.international ?? null,
          childcareAge: entry?.childcareAge ?? [],
          description: entry?.description?.rawHtml ?? "",
        },

        descriptionText: "",

        logo: ref(entry?.logo?.map(img => parseInt(img!.id!)) ?? []),
        images: ref(entry?.images?.map(img => parseInt(img!.id!)) ?? []),

        quillModules: {},

        quillFormats: [
          "bold", "italic", "link", "strike", "underline", "script",
          "header", "list", "align"
        ],

        municipalRegions, childcareAges,
        faArrowRight, faCircleNotch,
        resolvePhoneNumber, formatPhoneNumber,

        updateCoordinatesAbort: useAbortFn(
          async (signal) => await this._updateCoordinates(signal), true
        ),
        updateCoordinatesThrottle: useThrottleFn(
          async () => await this.updateCoordinatesAbort(), 10000, true, true
        ),
        updateCoordinatesDebounce: useDebounceFn(
          async () => await this.updateCoordinatesThrottle(),
          5000
        ),

        updateCoordinatesPromise: undefined as Promise<void> | undefined,

        submitting: false,
        formError: undefined as string | undefined,
      }
    },
    methods: {
      async updateCoordinates() {
        const form = (this.$refs.form! as FormInstance);
        if (
          form.states.city.value == null || form.states.city.value == ""
          || form.states.region.value == null || form.states.region.value == ""
          || form.states.addressLines.value == null || form.states.addressLines.value == ""
        ) { return; }

        const promise = this.updateCoordinatesDebounce();
        this.updateCoordinatesPromise = promise;
        await promise;
        if (this.updateCoordinatesPromise == promise) {
          this.updateCoordinatesPromise = undefined;
        }
      },
      async _updateCoordinates(signal: AbortSignal) {
        const form = (this.$refs.form! as FormInstance);
        if (
          form.states.city.value == null || form.states.city.value == ""
          || form.states.region.value == null || form.states.region.value == ""
          || form.states.addressLines.value == null || form.states.addressLines.value == ""
        ) { return; }

        const addressLines = (form.states.addressLines.value as string).trim().split("\n");
        if (addressLines.length === 0) { return; }
        const street = addressLines[addressLines.length - 1];

        if (signal.aborted) { return; }

        const result = await search({
          country: "ca",
          state: "on",
          city: form.states.city.value,
          county: form.states.region.value,
          postalcode: form.states?.postalCode?.value ?? undefined,
          street: street,
        }, { limit: 1 }, { signal: signal });

        let features = [] as Feature<Geometry, ResultGeoProperties>[];
        switch (result.type) {
          case "FeatureCollection": features = result.features; break;
          case "Feature": features = [result]; break;
          default: throw new Error(`unexpected geojson node type ${result.type}: ${result}`);
        }

        if (features.length > 0) {
          const feature = features[0];
          // nominatim always includes a bounding box
          const latitude = (feature.bbox![1] + feature.bbox![3]) / 2.0;
          const longitude = (feature.bbox![0] + feature.bbox![2]) / 2.0;

          form.setFieldValue("latitude", latitude);
          form.setFieldValue("longitude", longitude);
        }
      },

      resolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = {
          title: [], addressLines: [], city: [], region: [], postalCode: [],
          website: [], email: [], phone: [], fax: [],
          latitude: [], longitude: [],
        };

        for (const [handle, label] of Object.entries({
          "title": "Title", "addressLines": "Address lines", "city": "City", "region": "Region",
          "postalCode": "Postal Code"
        })) {
          if (values[handle] == null || values[handle] === "") {
            errors[handle].push({ message: `${label} is a required field` });
          }
        }

        if (values.postalCode !== "" && values.postalCode != null && !isPostalCodeValid(values.postalCode)) {
          errors.postalCode.push({ message: "Postal code is not valid" });
        } else if (values.postalCode != null) {
          values.postalCode = formatPostalCode(values.postalCode);
          (this.$refs.form as FormInstance).setFieldValue("postalCode", values.postalCode);
        }

        // https://mathiasbynens.be/demo/url-regex
        if (values.website !== "" && values.website != null && !isUrlValid(values.website)) {
          errors.website.push({ message: "Website is not a valid URL" });
        }

        if (values.email !== "" && values.email != null && !isEmailValid(values.email)) {
          errors.email.push({ message: "Email address is not a valid email address" });
        }

        if (values.latitude != null && values.longitude == null) {
          errors.longitude.push({ message: "Longitude is required if latitude is given."})
        }

        if (values.latitude == null && values.longitude != null) {
          errors.latitude.push({ message: "Latitude is required if longitude is given."})
        }

        return { errors };
      },

      async submit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }

        this.submitting = true;

        if (this.updateCoordinatesPromise != null) {
          try {
            await this.updateCoordinatesPromise;
          } catch {}
        }

        let result: RequestResult;
        try {
          result = await this.$session.saveEntry({
            enabled: true,
            entryId: !this.createNew ? this.entryId : undefined,
            sectionId: this.sectionId,
            title: submit.states.title.value,
            fields: {
              operator: submit.states?.operator?.value ?? "",
              address: {
                fields: {
                  addressLines: submit.states.addressLines.value,
                  region: submit.states.region.value,
                  city: submit.states.city.value,
                  province: "on",
                  country: "ca",
                  postalCode: submit.states?.postalCode?.value ?? "",
                  coordinates: {
                    longitude: submit.states.longitude.value ?? "",
                    latitude: submit.states.latitude.value ?? "",
                  }
                }
              },
              website: submit.states?.website?.value ?? "",
              phone: submit.states?.phone?.value ? {
                number: submit.states?.phone?.value ?? "",
                region: "CA",
              } : "",
              fax: submit.states?.fax?.value ? {
                number: submit.states?.fax?.value ?? "",
                region: "CA",
              } : "",
              email: submit.states?.email?.value ?? "",

              childcareAge: submit.states?.childcareAge.value ?? [],
              description: submit.states?.description.value ?? "",
              logo: this.logo != null ? this.logo.map(img => img.toString()) : [],
              images: this.images != null ? this.images.map(img => img.toString()) : [],
            }
          });
        } finally {
          this.submitting = false;
        }
        if (result.errorCode !== undefined) {
          this.formError = result.message;
        } else {
          if (this.createNew) {
            location.href = (result.model as any).url;
          } else {
            location.reload();
          }
        }
      },
    },
  });
</script>

<template>
  <Message class="full" severity="secondary" size="small" variant="simple">
    Please <a href="/en-ca/members/update-my-listing" target="_blank">read this page</a> for full details and questions on how to list your childcare centre.
  </Message>

  <Form v-slot="form" class="form" ref="form" :initial-values="initialValues"
    validate-on-value-update validate-on-submit validate-on-blur
    :resolver="resolve" @submit="submit">
    <div class="full">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-title`" name="title" fluid required :disabled="submitting"/>
        <label :for="`${vid}-title`">
          Centre Name<!--
          --><span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.title?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.title.error.message }}
      </Message>
    </div>

    <h3 class="full">Location Information</h3>
    <div class="double">
      <FloatLabel variant="on">
        <Textarea :id="`${vid}-addressLines`" name="addressLines" fluid required :disabled="submitting"
          rows="3" style="resize: none; grid-column-end: span 2;" placeholder="Unit 1
123 Main Street"
          @blur="updateCoordinates"/>
        <label :for="`${vid}-addressLines`">
          Address Lines <span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message class="full" severity="secondary" size="small" variant="simple">
        All address lines as written on mail, excluding lines for city, country, or postal code.
      </Message>

      <Message v-if="(form.addressLines?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.addressLines.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-city`" name="city" fluid required :disabled="submitting"
          @blur="updateCoordinates"/>
        <label :for="`${vid}-city`">
          City <span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.city?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.city.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <Select :id="`${vid}-region`" name="region" fluid required :disabled="submitting"
          :options="Object.entries(municipalRegions)"
          :option-value="d => d[0]" :option-label="d => d[1].name"
          filter filter-placeholder="Search"
          @update:model-value="updateCoordinates"
          :pt="{ listContainer: { style: { 'max-height': '25rem' }} }">
          <template #option="slot">
            <div style="display: flex; flex-direction: column;">
              <span>{{ slot.option[1].name }}</span>
              <small>{{ slot.option[1].long }}</small>
            </div>
          </template>
        </Select>
        <label :for="`${vid}-region`">
          Municipal Region <span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message severity="secondary" size="small" variant="simple">
        Uncertain about which region your centre is in? <a href="https://www.amo.on.ca/about-us/municipal-101/ontario-municipalities" target="_blank">Identify your municipality by city.</a>
      </Message>

      <Message v-if="(form.region?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.region.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-province`" class="readonly" readonly default-value="Ontario" fluid></InputText>
        <label :for="`${vid}-province`">
          Province <span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-country`" class="readonly" readonly default-value="Canada" fluid></InputText>
        <label :for="`${vid}-country`">
          Country <span class="required-star" aria-label="Required field, cannot be changed" title="Required field, cannot be changed"></span>
        </label>
      </FloatLabel>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-postalCode`" name="postalCode" fluid required :disabled="submitting"/>
        <label :for="`${vid}-postalCode`">
          Postal Code <span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.postalCode?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.postalCode.error.message }}
      </Message>
    </div>

    <div class="break">
      <FloatLabel variant="on">
        <IconField>
          <InputIcon v-if="updateCoordinatesPromise != null">
            <Icon :icon="faCircleNotch" spin/>
          </InputIcon>
          <InputNumber :id="`${vid}-latitude`" name="latitude" fluid :disabled="submitting"
            mode="decimal" show-clear
            :minFractionDigits="4" :maxFractionDigits="7" :max="90" :min="-90"
            @value-change="($refs?.form as FormInstance | undefined)?.validate('longitude')" />
        </IconField>
        <label :for="`${vid}-latitude`">Latitude</label>
      </FloatLabel>

      <Message v-if="(form.latitude?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.latitude.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <IconField>
          <InputIcon v-if="updateCoordinatesPromise != null">
            <Icon :icon="faCircleNotch" spin/>
          </InputIcon>
          <InputNumber :id="`${vid}-longitude`" name="longitude" fluid :disabled="submitting"
            mode="decimal" show-clear
            :minFractionDigits="4" :maxFractionDigits="7" :max="180" :min="-180"
            @value-change="($refs?.form as FormInstance | undefined)?.validate('latitude')" />
        </IconField>
        <label :for="`${vid}-longitude`">Longitude</label>
      </FloatLabel>

      <Message v-if="(form.longitude?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.longitude.error.message }}
      </Message>
    </div>

    <div class="full">
      <Message severity="secondary" size="small" variant="simple" style>
        Coordinate and map data © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors.
      </Message>
      <Message severity="secondary" size="small" variant="simple" style>
        Is your centre's address or street not present on the map? <a href="https://www.onosm.org/" target="_blank">Add it.</a>
      </Message>
    </div>

    <h3 class="full">Contact Information</h3>
    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-operator`" name="operator" fluid :disabled="submitting" />
        <label :for="`${vid}-operator`">Operator Name</label>
      </FloatLabel>

      <Message severity="secondary" size="small" variant="simple">
        The name of the person operating or managing the location.
      </Message>

      <Message v-if="(form.operator?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.operator.error.message }}
      </Message>
    </div>

    <div class="break">
      <PrimeFormField name="phone"
        :resolver="o => {
          ($refs.form as FormInstance).setFieldValue('phone', formatPhoneNumber(o.value));
          return resolvePhoneNumber(o, 'Phone number');
        }">
        <FloatLabel variant="on">
          <InputText :id="`${vid}-phone`" name="phone" inputmode="tel" fluid :disabled="submitting" />
          <label :for="`${vid}-phone`">Phone Number</label>
        </FloatLabel>
      </PrimeFormField>

      <Message v-if="(form.phone?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.phone.error.message }}
      </Message>
    </div>

    <div>
      <PrimeFormField name="fax"
        :resolver="o => {
          ($refs.form as FormInstance).setFieldValue('fax', formatPhoneNumber(o.value));
          return resolvePhoneNumber(o, 'Fax number');
        }">
        <FloatLabel variant="on">
          <InputText :id="`${vid}-fax`" name="fax" inputmode="tel" fluid :disabled="submitting" />
          <label :for="`${vid}-fax`">Fax Number</label>
        </FloatLabel>
      </PrimeFormField>

      <Message v-if="(form.fax?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.fax.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${vid}-website`" name="website" fluid :disabled="submitting" />
        <label :for="`${vid}-website`">Website</label>
      </FloatLabel>

      <Message v-if="(form.website?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.website.error.message }}
      </Message>
    </div>

    <div class="break">
      <FloatLabel variant="on">
        <InputText :id="`${vid}-email`" name="email" fluid :disabled="submitting" />
        <label :for="`${vid}-email`">Email Address</label>
      </FloatLabel>

      <Message severity="secondary" size="small" variant="simple">
        Visitors will be able to send your centre a message without revealing this email address.
      </Message>

      <Message v-if="(form.email?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.email.error.message }}
      </Message>
    </div>

    <h3 class="full">Additional Details</h3>

    <div>
      <FloatLabel variant="on">
        <MultiSelect :id="`${vid}-childcareAge`" name="childcareAge" fluid :disabled="submitting"
          :options="Object.entries(childcareAges)"
          :option-value="d => d[0]" :option-label="d => d[1].label"
          :showToggleAll="false" showClear
          :max-selected-labels="2" selected-items-label="{} age groups">
          <template #option="slotProps">
            <span>{{ slotProps.option[1].label }}</span>
            <small>{{ slotProps.option[1].hint }}</small>
          </template>
        </MultiSelect>
        <label :for="`${vid}-childcareAge`">Age Groups Served</label>
      </FloatLabel>

      <Message v-if="(form.childcareAge?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.childcareAge.error.message }}
      </Message>
    </div>


    <div class="full">
      <label>Centre Description</label>
      <Editor :id="`${vid}-description`" name="description" fluid
        :inert="submitting" :aria-disabled="submitting"
        editorStyle="min-height: 40ex;"
        :formats="quillFormats"
        @load="descriptionText = $event.instance.getText().trim()"
        @text-change="descriptionText = $event.textValue;">
        <template #toolbar>
          <span class="ql-formats">
            <select v-tooltip.bottom="'Size'" class="ql-header">
              <option selected/>
              <option value="2">Heading</option>
              <option value="3">Subheading</option>
            </select>
          </span>
          <span class="ql-formats">
            <button v-tooltip.bottom="'Bold'" class="ql-bold"/>
            <button v-tooltip.bottom="'Italic'" class="ql-italic"/>
            <button v-tooltip.bottom="'Underline'" class="ql-underline"/>
            <button v-tooltip.bottom="'Strikethrough'" class="ql-strike"/>
            <button v-tooltip.bottom="'Subscript'" class="ql-script" value="sub"/>
            <button v-tooltip.bottom="'Superscript'" class="ql-script" value="super"/>
            <button v-tooltip.bottom="'Link'" class="ql-link"/>
          </span>
          <span class="ql-formats">
            <button v-tooltip.bottom="'Ordered list'" class="ql-list" value="ordered"/>
            <button v-tooltip.bottom="'Bullet list'" class="ql-list" value="bullet"/>
            <button v-tooltip.bottom="'Remove formatting'" class="ql-clean"/>
          </span>
        </template>
      </Editor>

      <Message v-if="descriptionText.match(/location|(?:\d+,?\s+[^,\n]{2,20}?,?\s+(?:avenue|ave|boulevard|blvd|court|crt|cove|cove|crescent|cres|cul-de-sac|cds|drive|dr|knoll|knoll|landing|landng|lane|lane|loop|loop|meadow|meadow|moor|moor|park|pk|parkway|pky|passage|pass|plateau|plat|plaza|plaza|promenade|prom|range|rg|ridge|ridge|road|rd|route|rte|square|sq|street|st|subdivision|subdiv|terrace|terr|vale|vale|villas|villas|vista|vista|wharf|wharf)(?:[\s.,;:!<>')]|$))/gi) !== null"
        class="full" severity="warn" size="small" variant="simple">
        Please do not include listings for multiple/other locations in your description. This harms the discoverability of your other locations! They won't be searchable and they won't appear on the map! Instead, create a seperate centre directory entry for each location.
      </Message>

      <Message v-if="(form.username?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.title.error.message }}
      </Message>
    </div>

    <div class="full">
      <label>Centre Logo</label>

      <Message severity="secondary" size="small" variant="simple">
        Logo of your childcare centre
      </Message>

      <AssetsUpload volume="user" v-model:assets="logo" :disabled="submitting"
        accept="image/*" :maxFileSize="1024 * 1024 * 20" :assetLimit="1"/>

      <Message v-if="(form.images?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.images.error.message }}
      </Message>
    </div>

    <div class="full">
      <label>Centre Images</label>

      <Message severity="secondary" size="small" variant="simple">
        Images of your childcare centre, logos, etc.
      </Message>

      <AssetsUpload volume="user" v-model:assets="images" :disabled="submitting"
        accept="image/*" :maxFileSize="1024 * 1024 * 20" :assetLimit="10"/>

      <Message v-if="(form.images?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.images.error.message }}
      </Message>
    </div>

    <Message class="full" v-if="formError !== undefined"
      severity="error" size="small" variant="simple">
      {{ formError }}
    </Message>

    <Button class="full" type="submit" :label="createNew ? 'Create' : 'Save'" icon-pos="right"
      formnovalidate :disabled="!form.valid || submitting">
      <template #icon="slot">
        <Icon :class="[slot.class]" :icon="submitting ? faCircleNotch : faArrowRight" :spin="submitting"/>
      </template>
    </Button>
  </Form>
</template>

<style lang="less" scoped>
  h3 { text-align: center; }

  .form {
    display: grid;
    grid: auto-flow / 1fr 1fr;
    margin-block: var(--spacing-block);
    width: 100%;
    max-width: 90ch;

    gap: var(--spacing-block);
    align-items: flex-start;
    justify-items: stretch;

    & .break { grid-column-start: 1; }
    & .full { grid-column: 1 / span 2; }
    & .double { grid-row-end: span 2; }
  }

  .required-star::before {
    content: "*";
    color: var(--p-floatlabel-invalid-color);
  }

  .readonly {
    border: none;
    box-shadow: none;
    pointer-events: none;
  }
</style>
